import { useEffect, useState } from "react";
import { InlinePdfView } from "@/components/pdf/InlinePdfView";
import { Button } from "@/components/ui/Button";
import { Dialog } from "@/components/ui/Dialog";
import { Spinner } from "@/components/ui/Spinner";
import { fetchVeranstaltungAttachment } from "@/lib/veranstaltungen";

export interface RegieprotokollPdfDialogProps {
  open: boolean;
  mailId: number | null;
  attachmentId: string | null;
  title: string;
  onClose: () => void;
}

function asPdfBlob(blob: Blob): Blob {
  return blob.type === "application/pdf" ? blob : new Blob([blob], { type: "application/pdf" });
}

export function RegieprotokollPdfDialog({
  open,
  mailId,
  attachmentId,
  title,
  onClose,
}: RegieprotokollPdfDialogProps) {
  const [blob, setBlob] = useState<Blob | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open || mailId == null || !attachmentId) {
      setBlob(null);
      setError(null);
      setLoading(false);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError(null);
    setBlob(null);

    void fetchVeranstaltungAttachment(mailId, attachmentId)
      .then((next) => {
        if (cancelled) return;
        setBlob(asPdfBlob(next));
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : "PDF konnte nicht geladen werden.");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [open, mailId, attachmentId]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={title || "Regieprotokoll"}
      description="Regieprotokoll"
      size="4xl"
      fullScreenMobile
      footer={
        <div className="flex justify-end gap-2">
          <Button type="button" variant="secondary" onClick={onClose}>
            Schließen
          </Button>
        </div>
      }
    >
      <div className="min-h-[min(72vh,56rem)]">
        {loading ? (
          <div className="flex min-h-[min(72vh,56rem)] items-center justify-center py-16">
            <Spinner />
            <span className="ml-2 text-sm text-label-secondary">PDF wird geladen …</span>
          </div>
        ) : null}
        {error ? (
          <p className="flex min-h-[12rem] items-center justify-center px-4 py-16 text-center text-sm text-status-red">
            {error}
          </p>
        ) : null}
        {blob && !loading && !error ? (
          <InlinePdfView
            blob={blob}
            title={title || "Regieprotokoll"}
            className="min-h-[min(72vh,56rem)] w-full flex-1 rounded-xl border border-separator"
          />
        ) : null}
      </div>
    </Dialog>
  );
}
